import { useMutation } from "@apollo/client";
import { showSnackbar } from "@/lib/snackbarUtils";
import { CREATE_JOB_POST_MUTATION } from "@/components/Forms/JobPostForm/queries";
import usePageTitle from "@/hooks/usePageTitle";
import { useParams, useNavigate } from "react-router-dom";
import JobPostForm from "@/components/Forms/JobPostForm";
import { JobPostFormInput } from "@/components/Forms/JobPostForm/types";
import { useRef, useState, useEffect, useMemo } from "react";
import useConvertedJobData from "@/hooks/useConvertedJobData";
import { fullyDecodeHtmlEntities } from "@/lib/htmlHelpers";
import { ApplicationQuestion, Option, PostStage } from "@/pages/_layouts/jobs/types";
import {
  formatSubmissionDeadlineForMutation,
  isAdvancedOrExecutiveWithQuestions,
  isSubmissionDeadlineInPast
} from "@/components/Forms/JobPostForm/utils";

export default function CreateJobPostPage() {
  usePageTitle("/sections/[sectionId]/jobs/job-post/create", "Jobs | Create Job Post");
  const { sectionId: sectionIdParam } = useParams();
  const formRef = useRef<{ getValues: () => JobPostFormInput } | null>(null);
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSecondarySubmitting, setIsSecondarySubmitting] = useState(false);
  const { convertedJobData } = useConvertedJobData();
  const [errorBanner, setErrorBanner] = useState<string | null>(null);
  const [errorFacilityId, setErrorFacilityId] = useState<string | null>(null);

  const [createJobPost] = useMutation(CREATE_JOB_POST_MUTATION);

  const confettiTriggeredRef = useRef(false);
  const [triggerConfettiInForm, setTriggerConfettiInForm] = useState(false);

  const defaultValuesForForm = useMemo(() => {
    if (!convertedJobData) {
      if (sectionIdParam) {
        return { sectionId: sectionIdParam, name: "" };
      }
      return null;
    }
    const clonedData = JSON.parse(JSON.stringify(convertedJobData));

    const jobNameToUse = clonedData.jobTitle || clonedData.name || "";

    return {
      ...clonedData,
      name: jobNameToUse,
      jobTitle: undefined,
      sectionId: sectionIdParam || clonedData.sectionId || ""
    };
  }, [convertedJobData, sectionIdParam]);

  const hasJobImportData = defaultValuesForForm && Object.keys(defaultValuesForForm).length > 0 && convertedJobData;

  useEffect(() => {
    if (hasJobImportData && convertedJobData && !confettiTriggeredRef.current) {
      setTriggerConfettiInForm(true);
      confettiTriggeredRef.current = true;
      localStorage.removeItem("convertedJobData");
    }
  }, [defaultValuesForForm, hasJobImportData, convertedJobData]);

  async function handleSubmit(data: any, isSecondaryAction?: boolean) {
    if (isSecondaryAction) {
      setIsSecondarySubmitting(true);
    } else {
      setIsSubmitting(true);
    }
    try {
      const {
        name,
        overview,
        submissionDeadline,
        compensation,
        pointOfContact,
        requiredExperienceInYears,
        requiredEducation,
        additionalPgaEducation,
        ...inputData
      } = data as JobPostFormInput;

      const decodedOverview = overview ? fullyDecodeHtmlEntities(overview) : "";
      let stage = data.sendForReview
        ? PostStage.IN_REVIEW
        : data.isValid && !data.isDraft
          ? PostStage.ACTIVE
          : PostStage.DRAFT;

      const payload: any = {
        ...inputData,
        name,
        overview: decodedOverview,
        stage: stage,
        requiredExperienceInYears: parseInt(requiredExperienceInYears, 10),
        requiredEducation: requiredEducation === "N/A" ? null : requiredEducation,
        additionalPgaEducation,
        compensation: {
          baseMaximum: compensation.baseMaximum !== undefined ? compensation.baseMaximum / 100 : null,
          baseMinimum: compensation.baseMinimum !== undefined ? compensation.baseMinimum / 100 : null,
          totalAnticipated:
            typeof compensation.totalAnticipated === "number" ? compensation.totalAnticipated / 100 : null,
          currencyUnit: "USD"
        },
        pointOfContact: {
          name: pointOfContact?.name || "",
          email: pointOfContact?.email || "",
          phone: pointOfContact?.phone || "",
          title: pointOfContact?.title || ""
        }
      };

      payload.employerId = payload.employerId || null;

      if (submissionDeadline && submissionDeadline.startDate) {
        payload.submissionDeadline = formatSubmissionDeadlineForMutation(submissionDeadline.startDate);

        const deadlineInPast = isSubmissionDeadlineInPast(submissionDeadline.startDate);
        if (deadlineInPast) {
          payload.stage = PostStage.UNPOSTED;
        }
      } else {
        payload.submissionDeadline = null;
      }

      const applicationPayload = {
        questions: data.application?.questions.map((question: ApplicationQuestion, index: number) => ({
          content: question.content,
          position: index + 1,
          questionType: question.questionType,
          options: question.options.map((option: Option, optionIndex: number) => ({
            content: option.content,
            position: optionIndex + 1
          }))
        }))
      };

      // Remove form-specific fields that should not be sent to the API
      delete payload.facility;
      delete payload.facilityName;
      delete payload.sectionId;
      delete payload.locationType;
      delete payload.consultant;
      delete payload.employerData;
      delete payload.isValid;
      delete payload.isDraft;
      delete payload.application;

      const response = await createJobPost({
        variables: {
          input: payload,
          ...(isAdvancedOrExecutiveWithQuestions(data) ? { application: applicationPayload } : {})
        }
      });

      if (response.data.createJobPost.success) {
        navigate(
          `/sections/${sectionIdParam}/jobs/job-post/${response.data.createJobPost.jobPost.id}/details?confetti=true`
        );
        const successText = inputData.sendForReview
          ? "Job post successfully created and sent for review!"
          : "Job post created successfully!";
        showSnackbar(data.isDraft ? "Job post drafted successfully!" : successText);
      } else {
        const errorMessage = response.data.createJobPost.message;

        // Check if this is the specific employer permissions error
        if (errorMessage === "Employer does not have permissions at assigned facility." && data.facilityId) {
          setErrorBanner("Employer does not have Manage Job role at assigned facility.");
          setErrorFacilityId(data.facilityId);
        } else {
          setErrorBanner(errorMessage);
          setErrorFacilityId(null);
        }
        showSnackbar(response.data.createJobPost.message);
      }
    } catch (e) {
      setErrorBanner("Error creating job post");
    } finally {
      setIsSecondarySubmitting(false);
      setIsSubmitting(false);
    }
  }

  return (
    <JobPostForm
      onSubmit={handleSubmit}
      formRef={formRef}
      isSubmitting={isSubmitting}
      isSecondarySubmitting={isSecondarySubmitting}
      defaultValues={defaultValuesForForm}
      triggerConfetti={triggerConfettiInForm}
      errorBanner={errorBanner || undefined}
      setErrorBanner={(errorBanner) => setErrorBanner(errorBanner || null)}
      errorFacilityId={errorFacilityId}
      setErrorFacilityId={(facilityId) => setErrorFacilityId(facilityId || null)}
      onConfettiComplete={() => {
        setTriggerConfettiInForm(false);
      }}
    />
  );
}
